// Turns a service name (from a deal, a lead or a picker) into the fields of a
// quotation line, using the Product & Services master data. Nothing is invented:
// a service that is not in master data gets rate 0 and a flag so the form can say so.

export interface MasterServiceRecord {
  _id?: string
  id?: string
  name: string
  description?: string
  defaultRate?: number
  /** Minimum selling rate allowed after discount; 0/undefined = no minimum configured. */
  baseRate?: number
  /** Plain code, when the API sends one. */
  hsnSac?: string
  /** The HSN/SAC master record (populated by the API) or its id. */
  hsnSacId?: { code?: string; taxMasterId?: TaxSlabRef } | string | null
  unit?: string
  defaultUnit?: string
  /** The unit master record (populated by the API) or its id. */
  unitId?: { displayName?: string; name?: string; shortCode?: string } | string | null
  taxMasterId?: TaxSlabRef
}

/** A tax slab as the API sends it: populated (`_id`, `rate`, `name`) or as a bare id. */
export type TaxSlabRef = { _id?: string; id?: string; rate?: number } | string | null

export interface HsnRecord {
  _id?: string
  id?: string
  code: string
  taxMasterId?: TaxSlabRef
}

export interface ServiceLineFields {
  name: string
  description: string
  hsnSac: string
  unit: string
  rate: number
  gstRate: number
  /** The Taxes / GST slab the rate came from, '' when master data names none. */
  taxMasterId: string
  /** True when the service was not found (or is switched off) in master data. */
  missingFromMaster: boolean
}

const DEFAULT_GST = 18
const MISSING_UNIT = 'Nos'

export function findMasterService<T extends { name: string }>(services: T[], name: string): T | undefined {
  const wanted = String(name ?? '').trim().toLowerCase()
  return wanted ? services.find((s) => String(s.name ?? '').trim().toLowerCase() === wanted) : undefined
}

const rateOf = (tax: unknown): number | null =>
  tax && typeof tax === 'object' && Number.isFinite(Number((tax as any).rate)) ? Number((tax as any).rate) : null

// The slab arrives populated ({ _id, rate, name }) from the quotation-config bundle,
// or as a bare id from a stored document.
export function resolveTaxMasterId(tax: unknown): string {
  if (!tax) return ''
  if (typeof tax === 'object') return String((tax as any)._id ?? (tax as any).id ?? '')
  return String(tax)
}

const idOf = resolveTaxMasterId

/** The line fields for a service, from its master-data record. */
export function serviceLineFields(name: string, services: MasterServiceRecord[], hsnCodes: HsnRecord[] = []): ServiceLineFields {
  const service = findMasterService(services, name)
  if (!service) {
    return { name, description: name, hsnSac: '', unit: MISSING_UNIT, rate: 0, gstRate: DEFAULT_GST, taxMasterId: '', missingFromMaster: true }
  }

  const hsnRef = service.hsnSacId
  const hsnRecord =
    hsnRef && typeof hsnRef === 'object'
      ? hsnRef
      : hsnCodes.find((h) => String(h._id ?? h.id) === String(hsnRef ?? ''))
  const hsnSac = (hsnRecord as any)?.code || service.hsnSac || ''

  const unitRef = service.unitId
  const unit =
    (unitRef && typeof unitRef === 'object' ? unitRef.displayName || unitRef.name || unitRef.shortCode : '') ||
    service.unit ||
    service.defaultUnit ||
    MISSING_UNIT

  // The service's own tax, then its HSN/SAC code's tax, then the standard 18%.
  const serviceTaxId = idOf(service.taxMasterId)
  const hsnTaxId = idOf((hsnRecord as any)?.taxMasterId)
  const gstRate = rateOf(service.taxMasterId) ?? rateOf((hsnRecord as any)?.taxMasterId) ?? DEFAULT_GST

  return {
    name: service.name,
    // The master-data description is not carried onto a line: documents print the name and the rep's own note.
    description: service.name,
    hsnSac,
    unit,
    rate: Number(service.defaultRate) || 0,
    gstRate,
    taxMasterId: serviceTaxId || hsnTaxId,
    missingFromMaster: false,
  }
}
